import React from "react";
import { useT } from "@/lib/LanguageContext";
import { useTenant } from "@/lib/TenantContext";
// Relative, not "@/...": real modules under Jest, no alias there.
import { resolveBrandName } from "../../lib/siteLinks";
import { MARKETING_OPT_IN } from "../../constants/testIds/marketingOptIn";

/**
 * MarketingOptIn — the OPTIONAL "send me news and offers from {practice}" box on a booking
 * or a client checkout (owner decision 2026-10-21).
 *
 * CONTROLLED, AND NEVER TICKED BY THIS COMPONENT. The caller owns the boolean and starts
 * it at `false`: Canada's CASL (and GDPR art. 7) want a box the client ticks themselves,
 * labelled with who will write and what they will send. The label names the practice the
 * same way every brand surface does (`resolveBrandName`).
 *
 * The tick is only evidence until the booking exists or the sale is paid — the server
 * records it as consent then (`backend/modules/crm/consent.py`), and an unticked box
 * changes nothing on the client's file.
 *
 * Props:
 *   form      the backend's opt-in form name (booking | consult | product | program |
 *             pack | course) — names the testid, one box per form
 *   checked   boolean, owned by the caller
 *   onChange  (boolean) => void
 *   disabled  a request is in flight
 */
export default function MarketingOptIn({ form, checked, onChange, disabled = false }) {
  const t = useT("public");
  const { tenant } = useTenant();
  const practice = resolveBrandName(tenant, t("marketingOptIn.practiceFallback"));
  return (
    <label className="flex items-start gap-2 text-sm text-eh-ink cursor-pointer text-left">
      <input
        type="checkbox"
        checked={checked === true}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        data-testid={MARKETING_OPT_IN.checkbox(form)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-eh-sage-deep"
      />
      <span>
        {t("marketingOptIn.label", { practice })}
        <span className="block text-xs text-eh-muted mt-0.5">{t("marketingOptIn.hint")}</span>
      </span>
    </label>
  );
}
